import type { CommsAllowance, ConnectedAccount } from '@salonomy/types';
import { Button, Card, Pill, Spinner } from '@salonomy/ui';
import type { PillTone } from '@salonomy/ui';
import {
  useAccountLink,
  useCommsAllowance,
  useConnectAccount,
  useCreateConnectAccount,
} from '../../data/integrations.js';
import { currencyFormat } from '../../lib/locale.js';
import { useToast } from '../../lib/toast.js';

/**
 * Integration health for the WS6 developer panel: Stripe Connect onboarding status with a
 * re-onboarding link, and comms-provider health (the paid-channel allowance meter). Read-only — the
 * panel never takes money or sends a message, it surfaces whether the integrations are live and
 * links out to Stripe's hosted onboarding to finish/repair setup. Owner/manager only.
 */
export function IntegrationsHealthPanel() {
  return (
    <Card>
      <div className="section-title">Integrations</div>
      <div className="muted" style={{ fontSize: 13, marginTop: 4, marginBottom: 12 }}>
        The health of the services that make Salonomy work — payments and messaging.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <StripeConnectRow />
        <CommsHealthRow />
      </div>
    </Card>
  );
}

const ACCOUNT_TONE: Record<ConnectedAccount['status'], PillTone> = {
  enabled: 'success',
  pending: 'neutral',
  restricted: 'danger',
  disabled: 'danger',
};

const ACCOUNT_LABEL: Record<ConnectedAccount['status'], string> = {
  enabled: 'Connected & taking payments',
  pending: 'Onboarding incomplete',
  restricted: 'Action needed',
  disabled: 'Disabled',
};

function StripeConnectRow() {
  const toast = useToast();
  const { data: account, isLoading, isError } = useConnectAccount();
  const createAccount = useCreateConnectAccount();
  const accountLink = useAccountLink();

  // Open the hosted onboarding link in a new tab. Creates the Connect account first if the salon has
  // never started — both calls are owner/manager-gated server-side.
  async function startOnboarding() {
    try {
      if (!account) await createAccount.mutateAsync();
      const link = await accountLink.mutateAsync({});
      window.open(link.url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Could not start Stripe onboarding. Try again in a moment.');
    }
  }

  const busy = createAccount.isPending || accountLink.isPending;
  const needsAttention =
    account != null &&
    (account.status !== 'enabled' || account.requirementsDue.length > 0 || !account.payoutsEnabled);

  return (
    <div
      style={{
        border: '1px solid var(--slm-border)',
        borderRadius: 10,
        padding: '12px 14px',
      }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>Stripe payments</div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            Card payments, deposits and payouts run through your connected Stripe account.
          </div>
        </div>
        {isLoading ? (
          <Spinner />
        ) : isError ? (
          <Pill tone="danger">Status unavailable</Pill>
        ) : account ? (
          <Pill tone={ACCOUNT_TONE[account.status]}>{ACCOUNT_LABEL[account.status]}</Pill>
        ) : (
          <Pill tone="neutral">Not connected</Pill>
        )}
      </div>

      {account ? (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          <Pill tone={account.chargesEnabled ? 'success' : 'neutral'}>
            {account.chargesEnabled ? 'Charges on' : 'Charges off'}
          </Pill>
          <Pill tone={account.payoutsEnabled ? 'success' : 'neutral'}>
            {account.payoutsEnabled ? 'Payouts on' : 'Payouts off'}
          </Pill>
          {account.requirementsDue.length > 0 ? (
            <Pill tone="danger" className="mono">
              {account.requirementsDue.length} requirement
              {account.requirementsDue.length === 1 ? '' : 's'} due
            </Pill>
          ) : null}
        </div>
      ) : null}

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
        <Button
          size="sm"
          variant={account ? 'soft' : 'primary'}
          onClick={startOnboarding}
          disabled={busy}
        >
          {account ? 'Manage on Stripe' : 'Connect Stripe'}
        </Button>
        {needsAttention ? (
          <span className="muted" style={{ fontSize: 12 }}>
            Finish onboarding on Stripe to take payments and receive payouts.
          </span>
        ) : null}
      </div>
    </div>
  );
}

function CommsHealthRow() {
  const { data: allowance, isLoading, isError } = useCommsAllowance();

  return (
    <div
      style={{
        border: '1px solid var(--slm-border)',
        borderRadius: 10,
        padding: '12px 14px',
      }}
    >
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div>
          <div style={{ fontWeight: 600 }}>Client messaging</div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            Email and push are always free and unmetered. SMS and WhatsApp draw down your monthly
            allowance.
          </div>
        </div>
        {isLoading ? (
          <Spinner />
        ) : isError ? (
          <Pill tone="danger">Status unavailable</Pill>
        ) : (
          <Pill tone="success">Email &amp; push live</Pill>
        )}
      </div>

      {allowance ? <CommsMeters allowance={allowance} /> : null}
    </div>
  );
}

function CommsMeters({ allowance }: { allowance: CommsAllowance }) {
  const smsAtCap = allowance.smsUsed >= allowance.smsAllowance && allowance.smsAllowance > 0;
  const waAtCap =
    allowance.whatsappUsed >= allowance.whatsappAllowance && allowance.whatsappAllowance > 0;

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Meter
          label="SMS"
          used={allowance.smsUsed}
          allowance={allowance.smsAllowance}
          atCap={smsAtCap}
        />
        <Meter
          label="WhatsApp"
          used={allowance.whatsappUsed}
          allowance={allowance.whatsappAllowance}
          atCap={waAtCap}
        />
      </div>
      <div className="muted" style={{ fontSize: 11.5, marginTop: 10 }}>
        Smart routing saved {poundsSaved(allowance.smartRoutingSavedPence)} this period by
        preferring free channels. At the cap we{' '}
        {allowance.atCapPolicy === 'pause'
          ? 'pause paid sends'
          : allowance.atCapPolicy === 'ask'
            ? 'ask before each paid send'
            : 'auto top-up'}{' '}
        — change this under Comms settings.
      </div>
    </div>
  );
}

function Meter({
  label,
  used,
  allowance,
  atCap,
}: {
  label: string;
  used: number;
  allowance: number;
  atCap: boolean;
}) {
  const pct = allowance > 0 ? Math.min(100, Math.round((used / allowance) * 100)) : 0;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12.5, fontWeight: 600 }}>{label}</span>
        <span className="mono" style={{ fontSize: 11.5 }}>
          {used} / {allowance}
        </span>
      </div>
      <div
        style={{
          height: 6,
          borderRadius: 999,
          background: 'var(--slm-border)',
          overflow: 'hidden',
          marginTop: 4,
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: atCap ? 'var(--slm-danger)' : 'var(--slm-cobalt)',
          }}
        />
      </div>
      {atCap ? (
        <div className="muted" style={{ fontSize: 11, marginTop: 3, color: 'var(--slm-danger)' }}>
          At cap
        </div>
      ) : null}
    </div>
  );
}

function poundsSaved(pence: number): string {
  return currencyFormat().format(pence / 100);
}
